import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Zap, ChevronDown } from 'lucide-react';

interface HeroSectionProps {
  onExplore: () => void;
}

export default function HeroSection({ onExplore }: HeroSectionProps) {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated background grid */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)`,
        backgroundSize: '60px 60px',
        opacity: 0.18,
      }} />

      {/* Radial glow */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 70% 50% at 50% 50%, oklch(0.65 0.32 280 / 0.12) 0%, transparent 70%)',
      }} />

      {/* Floating orbs */}
      {[...Array(3)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: `${180 + i * 80}px`,
            height: `${180 + i * 80}px`,
            background: i % 2 === 0
              ? 'radial-gradient(circle, oklch(0.65 0.32 280 / 0.08) 0%, transparent 70%)'
              : 'radial-gradient(circle, oklch(0.68 0.28 195 / 0.07) 0%, transparent 70%)',
            left: `${15 + i * 28}%`,
            top: `${20 + i * 15}%`,
          }}
          animate={{ y: [0, -20, 0], x: [0, 10, 0] }}
          transition={{ duration: 6 + i * 2, repeat: Infinity, ease: 'easeInOut', delay: i * 1.5 }}
        />
      ))}

      {/* Content */}
      <div className="relative z-10 text-center max-w-5xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/40 text-primary text-xs font-bold tracking-widest mb-6"
            style={{ fontFamily: 'JetBrains Mono, monospace', background: 'oklch(0.65 0.32 280 / 0.1)' }}
          >
            <Zap className="w-3 h-3" />
            AI DESIGN · MESHY 3D · FDM PRINT
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-5xl sm:text-7xl lg:text-8xl font-black tracking-tight leading-none mb-6"
          style={{ fontFamily: 'Orbitron, sans-serif' }}
        >
          <span className="text-foreground">DRUK</span>
          <br />
          <span style={{
            background: 'linear-gradient(135deg, var(--primary), var(--accent))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            FIGUREK 3D
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="text-muted-foreground text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
          style={{ fontFamily: 'Rajdhani, sans-serif' }}
        >
          Figurki zaprojektowane przez AI, wymodelowane w Meshy AI i wydrukowane z najwyższą precyzją.
          Sci-fi, Fantasy, Filmy, Bajki — a nawet Twój własny projekt.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button
            size="lg"
            onClick={onExplore}
            className="px-10 py-6 text-base font-bold tracking-widest"
            style={{
              fontFamily: 'Orbitron, sans-serif',
              fontSize: '0.8rem',
              background: 'linear-gradient(135deg, var(--primary), var(--accent))',
              boxShadow: '0 0 32px oklch(0.65 0.32 280 / 0.35)',
            }}
          >
            PRZEGLĄDAJ KOLEKCJĘ
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="px-10 py-6 text-base font-bold tracking-widest border-primary/40 text-primary hover:bg-primary/10"
            style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '0.8rem' }}
          >
            ZAMÓW WŁASNĄ
          </Button>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-16 flex items-center justify-center gap-12 flex-wrap"
        >
          {[
            { value: '100+', label: 'Figurek w kolekcji' },
            { value: '6', label: 'Kategorii' },
            { value: '0.1mm', label: 'Precyzja druku' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div
                className="text-3xl font-black text-primary"
                style={{ fontFamily: 'Orbitron, sans-serif' }}
              >
                {stat.value}
              </div>
              <div className="text-xs text-muted-foreground tracking-wider mt-1" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-muted-foreground/50 flex flex-col items-center gap-1 cursor-pointer"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        onClick={onExplore}
      >
        <span className="text-xs tracking-widest" style={{ fontFamily: 'JetBrains Mono, monospace' }}>SCROLL</span>
        <ChevronDown className="w-4 h-4" />
      </motion.div>
    </section>
  );
}
